import {
  type Dispatch,
  type SetStateAction,
  useContext,
  createContext,
  PropsWithChildren,
  useCallback,
  useState,
} from 'react';
import useField from '__hooks/useField';
import { ACTIONS_ON_FAILURE } from './ActionWizard/utilities';

export type ErrorNotificationSeverity =
  | 'error'
  | 'info'
  | 'inherit'
  | 'warning';

type ActionOnFailureCtx = {
  actionOnFailure: string | null;
  errorNotificationSeverityLevel: ErrorNotificationSeverity;
  setActionOnFailure: (val: string) => void;
  setErrorNotificationSeverityLevel: Dispatch<
    SetStateAction<ErrorNotificationSeverity>
  >;
  checkActionChanged: (val: string) => boolean;
  considerEmptyAnError: boolean;
  setConsiderEmptyAnError: (val: boolean) => void;
};

type ActionOnFailureProviderProps = {
  step?: any;
  doCheck?: boolean;
};

export const context = createContext<ActionOnFailureCtx>({
  actionOnFailure: null,
  errorNotificationSeverityLevel: 'inherit',
  setActionOnFailure: () => {},
  setErrorNotificationSeverityLevel: () => {},
  checkActionChanged: () => false,
  considerEmptyAnError: false,
  setConsiderEmptyAnError: () => {},
});

export const useActionOnFailureContext = () => {
  return useContext(context);
};

export const ActionOnFailureProvider = ({
  children,
  step,
  doCheck,
}: PropsWithChildren<ActionOnFailureProviderProps>) => {
  const [actionOnFailure, setActionOnFailure] = useField(
    step?.actionOnFailure || ACTIONS_ON_FAILURE.NOTIFY_PAUSE
  );
  const [errorNotificationSeverityLevel, setErrorNotificationSeverityLevel] =
    useState<ErrorNotificationSeverity>(
      step?.errorNotificationSeverityLevel || 'inherit'
    );

  const [considerEmptyAnError, setConsiderEmptyAnError] = useField(
    step?.considerEmptyAnError ?? true
  );

  const checkActionChanged = useCallback(
    (val: string | null) => {
      return doCheck ? val !== null && val !== actionOnFailure : false;
    },
    [actionOnFailure, doCheck]
  );

  const ctx = {
    actionOnFailure,
    errorNotificationSeverityLevel,
    setActionOnFailure,
    setErrorNotificationSeverityLevel,
    checkActionChanged,
    considerEmptyAnError,
    setConsiderEmptyAnError,
  };

  return <context.Provider value={ctx}>{children}</context.Provider>;
};
